import { Industry } from '__generated__/Industry';
import { PacklaneBrief } from '__generated__/PacklaneBrief';
import { PacklaneBriefFields } from '__generated__/PacklaneBriefFields';
import { Currency } from 'common/utils/currency';
import Attachments from 'projects/components/Attachments';
import { BudgetSummary } from 'projects/components/BriefFields/BudgetField';
import { IndustryFieldSummary } from 'projects/components/BriefFields/IndustryField';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { TimeframeSummary } from 'projects/components/TimeFrame';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import React, { useEffect } from 'react';
import ContentSetting from 'projects/components/ContentSetting';

export interface Props {
    brief: PacklaneBrief;
    industries: Industry[];
}

function SummaryPage(props: Props) {
    const { brief } = props;
    const fields = brief.fields as PacklaneBriefFields;

    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Summary',
        });
    }, []);

    function profileApproval() {
        return fields.profileApproval ? __('Yes') : __('No');
    }

    function handleEditClick(page: 'AboutYou' | 'Details' | 'TimeAndBudget') {
        trackEditBriefEvent({
            id: brief.id,
            page: page,
        });
    }

    function renderDetailsSection() {
        return (
            <SummaryCard
                heading={__('Tell us about your project')}
                onEditClick={() => handleEditClick('Details')}
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={__('Name your project')}>{fields.title}</SummaryCardItem>

                <SummaryCardItem title={__('Describe your project')}>
                    {formatAndLinkify(fields.description)}
                </SummaryCardItem>

                {fields.referenceFiles.length > 0 && (
                    <SummaryCardItem title={__('Add reference files')}>
                        {Attachments(fields.referenceFiles)}
                    </SummaryCardItem>
                )}

                {fields.inspirationFiles.length > 0 && (
                    <SummaryCardItem title={__('Add inspiration files')}>
                        {Attachments(fields.inspirationFiles)}
                    </SummaryCardItem>
                )}

                {fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any inspiration on the Web')}>
                        {linkify(fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        );
    }

    function renderTimeAndBudgetSection() {
        return (
            <SummaryCard
                heading={__('Your time & budget')}
                onEditClick={() => handleEditClick('TimeAndBudget')}
                url={routes.brief(brief, 2)}
            >
                <SummaryCardItem title={__("What's your timeline?")}>
                    <TimeframeSummary
                        value={fields.timeframe}
                        startDate={fields.startDate}
                        endDate={fields.endDate}
                    />
                </SummaryCardItem>
                <SummaryCardItem title={__("What's your budget?")}>
                    <BudgetSummary budget={fields.budget} currency={fields.currency as Currency} />
                </SummaryCardItem>
            </SummaryCard>
        );
    }

    function renderAboutYouSection() {
        const { brief } = props;

        return (
            <SummaryCard
                heading={__('About you')}
                onEditClick={() => handleEditClick('AboutYou')}
                url={routes.brief(brief, 3)}
            >
                {fields.industry && (
                    <IndustryFieldSummary
                        industries={props.industries}
                        industry={fields.industry}
                    />
                )}

                {fields.urls.length > 0 && (
                    <SummaryCardItem title={__('Add your website and/or social media pages')}>
                        {linkify(fields.urls)}
                    </SummaryCardItem>
                )}
                <SummaryCardItem
                    title={__(
                        'Can your designer showcase the finished designs in their 99designs portfolio?'
                    )}
                >
                    {profileApproval()}
                </SummaryCardItem>
                {fields.other != '' && (
                    <SummaryCardItem
                        title={__("Anything else you'd like to share with your designer?")}
                    >
                        {formatAndLinkify(fields.other)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        );
    }

    return (
        <ContentSetting>
            {renderDetailsSection()}
            {renderTimeAndBudgetSection()}
            {renderAboutYouSection()}
        </ContentSetting>
    );
}

export default SummaryPage;
